import LoadingSpinner from 'component/LoadingSpinner';
import MultiSelectDropdown from 'component/MultiSelectDropdown';
import { randomUUID } from 'crypto';
import { Value } from 'domain/model/Value';
import { ProjectTaskAssignByJobReq } from 'domain/payload/request/project/ProjectTaskAssignByJobReq';
import { ProjectTaskListResp } from 'domain/payload/response/project/ProjectTaskListResp';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import ProjectService from 'service/ProjectService';
import { v4 as uuidv4 } from 'uuid';

interface ProjectAssignModalProps {
  item: any;
  onClose: (decisionSuccess: boolean) => void;
}

const ProjectAssignModal: React.FC<ProjectAssignModalProps> = ({ item, onClose }) => {
  const { profile } = useSelector((state: any) => state.auth);

  const projectService = ProjectService();

  const [isLoadingDetail, setIsLoadingDetail] = useState(false);
  const [isLoadingDecision, setIsLoadingDecision] = useState(false);
  const [isLoadingUpdateValue, setIsLoadingUpdateValue] = useState(false);

  const [isLoadingConfigNewAssignment, setIsLoadingConfigNewAssignment] = useState(false);
  const [isLoadingTaskList, setIsLoadingTaskList] = useState(false);

  const [isLoadingSaveAssignment, setIsLoadingSaveAssignment] = useState(false);

  const [tabMode, setTabMode] = useState<string>('assignmentList');

  const [installerList, setInstallerList] = useState<Value[]>([]);
  const [installerId, setInstallerId] = useState('');

  const [taskList, setTaskList] = useState<ProjectTaskListResp[]>([]);
  const [replacementMap, setReplacementMap] = useState<Record<string, string>>({});
  const [originalInstallerMap, setOriginalInstallerMap] = useState<Record<string, string>>({});

  const [assignmentRequest, setAssignmentRequest] = useState<ProjectTaskAssignByJobReq>({
    jobId: item.id,
    processData: []
  });

  const fetchDetail = async () => {
    const token = profile?.data.token;
    if (!token) {
      window.alert('Token tidak ditemukan. Silakan login kembali.');
      return;
    }

    setIsLoadingDetail(true);
    try {

    } catch (err: any) {
      console.error(err);
      const errorMessage = err.response?.data?.message || err.message || 'Terjadi kesalahan saat mengambil detail';
      window.alert(`Error: ${errorMessage}`);
    } finally {
      setIsLoadingDetail(false);
    }
  };

  const fetchConfig = async () => {
    const token = profile?.data.token;
    if (!token) {
      window.alert('Token tidak ditemukan. Silakan login kembali.');
      return;
    }
    setIsLoadingConfigNewAssignment(true);
    try {
      const res = await projectService.projectTaskConfig(token, item.projectId);
      setInstallerList(res.data?.installerList || []);
    } catch (err: any) {
      console.error(err);
      const errorMessage = err.response?.data?.message || err.message || 'Terjadi kesalahan saat mengambil konfigurasi';
      window.alert(`Error: ${errorMessage}`);
    } finally {
      setIsLoadingConfigNewAssignment(false);
    }
  };

  const getInstallerNameById = (id: string): string => {
    const installer = installerList.find(i => i.id === id);
    return installer?.name || '';
  };

  const getPlaceholderForTask = (task: ProjectTaskListResp): string => {
    if (replacementMap[task.id]) {
      return getInstallerNameById(replacementMap[task.id]);
    }
    return task.installerName;
  };

  const fetchTaskList = async () => {
    const token = profile?.data.token;
    if (!token) {
      window.alert('Token tidak ditemukan. Silakan login kembali.');
      return;
    }

    setIsLoadingTaskList(true);
    try {
      const res = await projectService.projectTaskList(token, item.projectId, item.id, null, 1, 10000);
      const data = res.data || [];
      setTaskList(data);

      const map: Record<string, string> = {};
      data.forEach((t: any) => {
        map[t.id] = t.installerId;
      });
      setOriginalInstallerMap(map);
    } catch (err: any) {
      console.error(err);
      const errorMessage = err.response?.data?.message || err.message || 'Terjadi kesalahan saat mengambil daftar task';
      window.alert(`Error: ${errorMessage}`);
    } finally {
      setIsLoadingTaskList(false);
    }
  };

  const maxInput = Number(item?.maxInput ?? 0);
  const maxActiveTasks = 1;

  const isActiveTask = (task: ProjectTaskListResp): boolean => {
    if (task.statusKey !== undefined && task.statusKey !== null && task.statusKey !== 0) {
      return [1, 2, 3, 4].includes(Number(task.statusKey));
    }
    return task.status === "Assigned";
  };

  const activeTasks = taskList.filter(isActiveTask);
  const isAssignmentLimitReached = activeTasks.length >= maxActiveTasks;

  const handleAddAssignment = async () => {
    if (!installerId) {
      alert("Please select an installer");
      return;
    }

    if (activeTasks.length >= maxActiveTasks) {
      alert(`Satu job hanya boleh memiliki maksimal ${maxActiveTasks} assignment aktif`);
      return;
    }

    const isWorkerAlreadyAssigned = taskList.some(
      t => isActiveTask(t) && t.installerId === installerId
    );
    if (isWorkerAlreadyAssigned) {
      alert("Worker ini sudah memiliki assignment aktif pada job ini");
      return;
    }

    const newTaskId = uuidv4();
    const selectedInstaller = installerList.find(i => i.id === installerId);

    const newTask: ProjectTaskListResp = {
      id: newTaskId,
      name: item?.name || "",
      storeName: item?.placeName || "",
      installerId: selectedInstaller?.id || installerId,
      installerName: selectedInstaller?.name || "",
      createdAt: "Just now",
      status: "Assigned",
      statusKey: 1,
      goodsCounts: 0,
      createdBy: profile?.data?.name || "",
      resultCounts: 0
    };

    setTaskList(prev => [...prev, newTask]);
    setAssignmentRequest(prev => ({
      ...prev,
      processData: [
        ...prev.processData,
        {
          taskId: newTaskId,
          workerId: installerId,
          action: "create"
        }
      ]
    }));
    setInstallerId('');
  };

  const handleDeleteAssignment = async (taskId: string) => {
    const isNewTask = assignmentRequest.processData.some(
      p => p.taskId === taskId && p.action === "create"
    );

    if (isNewTask) {
      setAssignmentRequest(prev => ({
        ...prev,
        processData: prev.processData.filter(p => p.taskId !== taskId)
      }));
    } else {
      const task = taskList.find(t => t.id === taskId);
      setAssignmentRequest(prev => ({
        ...prev,
        processData: [
          ...prev.processData.filter(p => p.taskId !== taskId),
          {
            taskId,
            workerId: task?.installerId || "",
            action: "cancel"
          }
        ]
      }));
    }

    setReplacementMap(prev => {
      const next = { ...prev };
      delete next[taskId];
      return next;
    });

    setTaskList(prev => prev.filter(t => t.id !== taskId));
  };

  const handleReplaceChange = (taskId: string, selectedArr: string[]) => {
    const newInstallerId = selectedArr && selectedArr[0] ? selectedArr[0] : '';
    setReplacementMap(prev => ({
      ...prev,
      [taskId]: newInstallerId
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (activeTasks.length > maxActiveTasks) {
      alert(`Satu job hanya boleh memiliki maksimal ${maxActiveTasks} assignment aktif`);
      return;
    }

    setIsLoadingSaveAssignment(true);

    const finalProcessData = [...assignmentRequest.processData];

    taskList.forEach(task => {
      if (task.status !== "Assigned") return;
      const newInstaller = replacementMap[task.id];
      const originalInstaller = originalInstallerMap[task.id];
      if (newInstaller && newInstaller !== originalInstaller) {
        const existingIdx = finalProcessData.findIndex(p => p.taskId === task.id);
        if (existingIdx >= 0) {
          finalProcessData[existingIdx] = {
            taskId: task.id,
            workerId: newInstaller,
            action: "update"
          };
        } else {
          finalProcessData.push({
            taskId: task.id,
            workerId: newInstaller,
            action: "update"
          });
        }
      }
    });

    const payload: ProjectTaskAssignByJobReq = {
      ...assignmentRequest,
      processData: finalProcessData
    };

    try {
      await projectService.projectTaskAssignByJob(profile?.data.token, payload);
      alert(`Assignment Saved`);
      onClose(true);
    } catch (err: any) {
      console.error(err);
      const errorMessage = err.response?.data?.message || err.message || 'Terjadi kesalahan saat menyimpan assignment';
      alert(`Error: ${errorMessage}`);
    } finally {
      setIsLoadingSaveAssignment(false);
    }
  };

  // const projectAssign = async () => {
  //   const token = profile?.data.token;
  //   if (!token) return;

  //   try {
  //     const request: ProjectTaskAssignReq = {
  //       taskTypeKey: taskType!,
  //       projectId: id!,
  //       goodsIds: goodsIds,
  //       placeIds: storeIds,
  //       areaIds: areaIds,
  //       installerId: installerId,
  //       fee: fee,
  //       instructionFormId: instructionFormId,
  //       description: description
  //     };
  //     console.log(request)
  //     await projectService.projectTaskAssign(token, request);
  //   } catch (err) {
  //     console.error(err);
  //     throw err
  //   }
  // };

  const openGoogleMaps = async (lat: number, lng: number) => {
    const url = `https://www.google.com/maps?q=${lat},${lng}`;
    window.open(url, "_blank");
  }

  useEffect(() => {
    fetchDetail();
    fetchConfig();
    fetchTaskList();
  }, []);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !isLoadingDecision && !isLoadingUpdateValue) onClose(false);
    };
    document.addEventListener('keydown', handleEsc);
    return () => {
      document.removeEventListener('keydown', handleEsc);
    };
  }, [onClose, isLoadingDecision, isLoadingUpdateValue]);

  return (
    <>
      <div
        className="modal modal-lg show d-block"
        tabIndex={-1}
        role="dialog"
        style={{
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          position: 'fixed',
          inset: 0,
          zIndex: 1050,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
        onClick={() => {
          if (!isLoadingDecision && !isLoadingUpdateValue) onClose(false);
        }}
      >
        <div className="modal-dialog modal-lg" role="document" onClick={(e) => e.stopPropagation()}>
          <div className="modal-content" style={{ maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
            <div className="modal-body" style={{ overflowY: 'auto' }}>
              {
                isLoadingDetail || isLoadingConfigNewAssignment ? <LoadingSpinner /> : (
                  <div>
                    <h5 className="mb-1">{item.name}</h5>
                    <p className="text-muted" style={{ fontSize: '0.875rem' }}>
                      Description : {item.description}<br />
                      Project Name : {item.projectName}<br />
                      <span>
                        Assignment Aktif : {activeTasks.length}/{maxActiveTasks} {maxInput > 0 ? `(Max Input: ${maxInput})` : ''}<br />
                      </span>
                    </p>
                    <div className="mb-3">
                      <i className="fa fa-map-marker-alt me-2"></i> <a href="#" onClick={() => item.latitude != null && item.longitude != null ? openGoogleMaps(item.latitude, item.longitude) : null}>{item.latitude}, {item.longitude}</a>
                    </div>
                    <hr />
                    <div>

                      {/* <div className="d-flex mb-3">
                        <TabNavigation
                          activeTab={tabMode}
                          onTabChange={(tab) => {
                            setTabMode(tab);
                          }}
                          tabs={[
                            { key: "assignmentList", label: "Assignment List" },
                          ]}
                        />
                      </div> */}
                      {tabMode === "assignmentList" &&
                        <>
                          <div className="mb-3 text-right d-flex justify-content-end align-items-center gap-2">
                            {/* <label className="form-label fw-semibold m-0 me-2">New Assignment</label> */}
                            <div className="d-flex align-items-center" style={{ width: "75%" }}>
                              <div className="me-2 flex-grow-1">
                                <MultiSelectDropdown
                                  options={installerList.map(i => ({ code: i.id, name: i.name }))}
                                  placeholder="Choose Installer..."
                                  onChange={(e) => setInstallerId(e[0])}
                                  single={true}
                                  isSourceModal={true}
                                />
                              </div>
                              <button
                                className="btn btn-primary"
                                disabled={isAssignmentLimitReached}
                                title={isAssignmentLimitReached ? `Maksimal assignment aktif (${maxActiveTasks}) telah tercapai` : undefined}
                                onClick={() => {
                                  handleAddAssignment()
                                }}
                              >
                                Add Assignment
                              </button>
                            </div>
                          </div>
                          <table className="table table-condensed table-hover">
                            <thead className="table-primary">
                              <tr>
                                <th>ID</th>
                                <th>Installer Name</th>
                                <th>Assign At</th>
                                <th>Status</th>
                                <th>Result Count</th>
                                <th>Replace Assignment</th>
                                <th></th>
                              </tr>
                            </thead>
                            <tbody>

                              {taskList.length > 0 ? taskList.map((task, idx) => (
                                <tr key={task.id}>
                                  <td>{task.id}</td>
                                  <td>{task.installerName}</td>
                                  <td>{task.createdAt}</td>
                                  <td>{task.status}</td>
                                  <td>{task.resultCounts}</td>
                                  <td>
                                    {task.status === "Assigned" ? (
                                      <MultiSelectDropdown
                                        options={installerList.map(i => ({ code: i.id, name: i.name }))}
                                        placeholder={getPlaceholderForTask(task)} 
                                        onChange={(e) => handleReplaceChange(task.id, e)}
                                        single={true}
                                        isSourceModal={true}
                                        value={replacementMap[task.id] ? [replacementMap[task.id]] : []}
                                      />
                                    ) : (
                                      <span className="text-muted">Assignment Expired</span>
                                    )}
                                  </td>
                                  <td>
                                    <button className="btn btn-outline-danger btn-small-compact border-0"
                                      onClick={() => {
                                        handleDeleteAssignment(task.id);
                                      }}>
                                      <i className="fa-solid fa-user-xmark"></i>
                                    </button>
                                  </td>
                                </tr>
                              )) : (
                                <tr>
                                  <td colSpan={8} className="text-center">
                                    Tidak ada data
                                  </td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </>
                      }
                    </div>
                  </div>
                )
              }
            </div>

            <div className="modal-footer justify-content-between">
              <div className="d-flex gap-2"></div>
              <div className="d-flex gap-2">
                <button className="btn btn-outline-primary" onClick={() => onClose(false)}>
                  Cancel
                </button>
                <button 
                  className="btn btn-primary d-flex align-items-center justify-content-center" 
                  onClick={(e) => handleSubmit(e)} 
                  disabled={isLoadingSaveAssignment}
                  style={{
                    minHeight: '38px',
                    minWidth: '80px',
                    padding: '6px 16px'
                  }}
                >
                  {isLoadingSaveAssignment ? (
                    <>
                      <div 
                        className="spinner-border spinner-border-sm me-2" 
                        role="status"
                        style={{ 
                          width: '16px', 
                          height: '16px',
                          borderWidth: '0.15em'
                        }}
                      >
                        <span className="visually-hidden">Loading...</span>
                      </div>
                      <span>Saving...</span>
                    </>
                  ) : (
                    "Save"
                  )}
                </button>
                {/* {
                  status?.key === 3 && !isLoadingDecision && !isLoadingDetail && !isLoadingUpdateValue &&
                  <>
                    <button className="btn btn-danger" onClick={() => setShowRejectModal(true)}>
                      <i className="fa fa-times me-1"></i> Reject
                    </button>
                    <button className="btn btn-primary" onClick={() => handleDecision("approve", "")}>
                      <i className="fa fa-check me-1"></i> Approve
                    </button>
                  </>
                }
                {status?.key !== 3 && !isLoadingDecision && !isLoadingDetail && !isLoadingUpdateValue && (
                  <button
                    className="btn btn-warning"
                    onClick={handleBackToQc}
                  >
                    <i className="fa fa-undo me-1"></i> Back to QC
                  </button>
                )}
                {
                  isLoadingDecision &&
                  <div className="d-flex align-items-center">
                    <LoadingSpinner />
                    <div style={{ marginRight: 8 }} />
                    {decision == "approve" && "Approving.."}
                    {decision == "reject" && "Rejecting.."}
                    {decision == "back_to_qc" && "Sending back to QC.."}
                  </div>
                } */}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ProjectAssignModal;
